/**
 * Environment variable validation utilities
 * Ensures critical environment variables are properly configured
 */

/**
 * Validates that the API URL is properly configured and uses HTTPS in production
 * @param apiUrl - The API URL to validate
 * @returns The validated API URL
 * @throws Error if validation fails
 */
export const validateApiUrl = (apiUrl: string): string => {
  if (!apiUrl) {
    throw new Error('API_BASE_URL is not configured');
  }

  // In production, ensure HTTPS is used
  if (process.env.NODE_ENV === 'production') {
    if (!apiUrl.startsWith('https://')) {
      throw new Error(
        'API_BASE_URL must use HTTPS in production environment. ' +
        `Current URL: ${apiUrl}`
      );
    }
  }

  // Validate URL format
  try {
    new URL(apiUrl);
  } catch (error) {
    throw new Error(`Invalid API_BASE_URL format: ${apiUrl}`);
  }

  return apiUrl;
};

/**
 * Gets and validates the API base URL from environment variables
 * @returns The validated API base URL
 */
export const getValidatedApiUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';
  return validateApiUrl(apiUrl);
};
